import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";

import {
    Alert,
    Avatar,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    FormControlLabel,
    FormGroup,
    FormHelperText,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Paper,
    Select,
    Skeleton,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid";
import { alpha } from "@mui/material/styles";

import AccountBalanceOutlinedIcon from "@mui/icons-material/AccountBalanceOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import ContactMailOutlinedIcon from "@mui/icons-material/ContactMailOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import GavelOutlinedIcon from "@mui/icons-material/GavelOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import LabelOutlinedIcon from "@mui/icons-material/LabelOutlined";
import LoginOutlinedIcon from "@mui/icons-material/LoginOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";

import EditPasswordDialog from "../dialogs/EditPasswordDialog";
import EditUsernameDialog from "../dialogs/EditUsernameDialog";

import {

    toEmptyStringIfNull,
} from "../util/utilities";

import {
    handleResponse,
    handleValidationResponse,
} from "../util/integration";

import {
    disableAPI,
    blockBuyerAPI,
    fetchBlockedBuyersAPI,
    fetchBuyersAPI,
    fetchUserAPI,
    fetchUserProfileAPI,
    fetchRegistrationCourtsAPI,
    fetchRegistrationSectionsAPI,
    fetchUserCanBuyAPI,
    fetchUserCanSellAPI,
    saveBankAPI,
    saveBICAPI,
    saveBusinessNameAPI,
    saveCountryAPI,
    saveEmailAddressAPI,
    saveFirstNameAPI,
    saveAdditionalDirectorsAPI,
    saveHouseNoAPI,
    saveIBANAPI,
    saveImprintAPI,
    saveInvoicePrefixAPI,
    saveStripeIdAPI,
    saveIsAdminAPI,
    saveIsBuyerAPI,
    saveIsSellerAPI,
    saveLastNameAPI,
    saveMobilePhoneNumberAPI,
    saveOperatorPrivacyPolicyAPI,
    saveOperatorTermsAPI,
    savePasswordAPI,
    saveRegisteredAPI,
    saveRegistrationCourtAPI,
    saveRegistrationNoAPI,
    saveRegistrationSectionAPI,
    saveSellerPrivacyPolicyAPI,
    saveSellerTermsAPI,
    saveStreetAPI,
    saveTagsAPI,
    saveTaxExemptionAPI,
    saveTaxIdAPI,
    saveTownAPI,
    saveTransactionFeeAPI,
    saveUsernameAPI,
    saveVatIdAPI,
    saveVatRateAPI,
    saveZipCodeAPI,
    unblockBuyerAPI,
} from "../api/user_api";

import { fetchTagsAPI } from "../api/tag_api";

import {
    validateAcceptAny,
    validateNonEmptyAlphanumericString,
    validatePossiblyEmptyAlphanumericString,
    validatePossiblyEmptyEmailAddress,
    validatePossiblyEmptyHouseNo,
    validatePossiblyEmptyPhoneNumber,
    validatePossiblyEmptyStripeAccountId,
    validatePossiblyEmptyUstID,
    validatePossiblyEmptyZipCode,
} from "../api/validation_api";

import { PRIMARY_COLOR } from "../util/constants";

const EMPTY_VALUE = "–";
const PASSWORD_MASK = "••••••••";

const DIALOG = Object.freeze({
    businessName: "businessName",
    firstName: "firstName",
    lastName: "lastName",
    additionalDirectors: "additionalDirectors",
    emailAddress: "emailAddress",
    mobilePhoneNumber: "mobilePhoneNumber",
    street: "street",
    houseNo: "houseNo",
    zipCode: "zipCode",
    town: "town",
    country: "country",
    bank: "bank",
    bic: "bic",
    iban: "iban",
    vatId: "vatId",
    taxId: "taxId",
    vatRate: "vatRate",
    registrationCourt: "registrationCourt",
    registrationData: "registrationData",
    invoicePrefix: "invoicePrefix",
    stripeId: "stripeId",
    transactionFee: "transactionFee",
    tags: "tags",
    username: "username",
    password: "password",
    imprint: "imprint",
    partnerManagement: "partnerManagement",
});

const ACTION = Object.freeze({
    taxExemption: "taxExemption",
    registered: "registered",
    adminRole: "adminRole",
    buyerRole: "buyerRole",
    sellerRole: "sellerRole",
    registrationData: "registrationData",
    tags: "tags",
    imprint: "imprint",
    sellerTerms: "sellerTerms",
    sellerPrivacyPolicy: "sellerPrivacyPolicy",
    operatorTerms: "operatorTerms",
    operatorPrivacyPolicy: "operatorPrivacyPolicy",
    disable: "disable",
});

/*
 * Altdaten können noch den früher verwendeten Wert `STUTTGART` enthalten.
 * Beim Speichern wird immer der aktuelle Backend-Enumwert verwendet.
 */
const REGISTRATION_COURT_ALIASES = Object.freeze({
    AMTSGERICHT_STUTTGART:
        "AMTSGERICHT_STUTTGART",
    STUTTGART:
        "AMTSGERICHT_STUTTGART",
});

const LABEL = Object.freeze({
    active: "Aktiv",
    disabled: "Deaktiviert",
    refreshing: "Wird aktualisiert …",

    loadingError:
        "Die Benutzerdaten konnten nicht geladen werden.",

    refreshError:
        "Die Benutzerdaten konnten nicht vollständig aktualisiert werden.",

    accessTokenMissing:
        "Für den Zugriff fehlt ein gültiger Access Token.",

    administrator: "Administrator",
    buyer: "Käufer",
    seller: "Verkäufer",
    operator: "Operator",

    profile: "Profil",
    businessDetails: "Geschäftliche Angaben",
    sellerDocuments: "Verkäuferdokumente",
    operatorDocuments: "Betreiberdokumente",
    rolesAndStatus: "Rollen und Status",
    login: "Login",
    dangerZone: "Gefahrenbereich",

    person: "Person",
    contact: "Kontakt",
    address: "Adresse",
    tags: "Tags",

    bankAndTax: "Bank- und Steuerdaten",
    invoiceDetails: "Rechnungsangaben",
    registration: "Handelsregister",
    imprint: "Impressum",
    permissions: "Berechtigungen",
    loginData: "Zugangsdaten",
    documents: "Dokumente",

    company: "Firma",
    firstName: "Vorname",
    lastName: "Nachname",
    additionalDirectors: "Weitere Geschäftsführer",
    emailAddress: "E-Mail-Adresse",
    mobilePhoneNumber: "Handynummer",

    street: "Straße",
    houseNo: "Hausnummer",
    zipCode: "Postleitzahl",
    town: "Ort",
    country: "Land",

    bank: "Bank",
    bic: "BIC",
    iban: "IBAN",

    vatId: "USt-ID",
    taxId: "Steuernummer",
    vat: "Umsatzsteuer",
    vatRate: "Umsatzsteuersatz",

    taxExemption:
        "Umsatzsteuerbefreiung nach § 19 UStG",

    registered:
        "Im Handelsregister eingetragen",

    registrationCourt: "Registergericht",
    registrationSection: "Registerabteilung",
    registrationNo: "Registernummer",

    invoicePrefix: "Rechnungspräfix",
    stripeId: "Stripe-Konto-ID",
    transactionFee: "Transaktionsgebühr",

    username: "Benutzername",
    password: "Passwort",

    canBuy: "Kann kaufen",
    cannotBuy: "Kann nicht kaufen",
    canSell: "Kann verkaufen",
    cannotSell: "Kann nicht verkaufen",
    statusUnknown: "Status unbekannt",

    sellerTerms: "AGB",

    sellerPrivacyPolicy:
        "Datenschutzerklärung",

    operatorTerms: "AGB",

    operatorPrivacyPolicy:
        "Datenschutzerklärung",

    documentStored: "Dokument hinterlegt",
    documentMissing: "Nicht hinterlegt",

    download: "Herunterladen",
    upload: "PDF hochladen",
    replace: "PDF ersetzen",

    partnerManagement: "Partnermanagement",

    edit: "Bearbeiten",
    save: "Speichern",
    close: "Schließen",

    showSensitive:
        "Sensible Daten anzeigen",

    hideSensitive:
        "Sensible Daten ausblenden",

    disable: "Partner deaktivieren",

    disableDescription:
        "Der Partner kann sich nach der Deaktivierung nicht mehr anmelden.",
});

const MESSAGE = Object.freeze({
    invalidValue:
        "Der eingegebene Wert ist ungültig.",

    invalidPercentage:
        "Bitte eine ganze Prozentzahl zwischen 0 und 100 eingeben.",

    invalidPdf:
        "Bitte eine PDF-Datei auswählen.",

    tagsUnavailable:
        "Die verfügbaren Tags konnten nicht geladen werden.",

    registrationCourtsUnavailable:
        "Die Registergerichte konnten nicht geladen werden.",

    registrationSectionsUnavailable:
        "Die Registerabteilungen konnten nicht geladen werden.",

    businessNameSaved:
        "Firma gespeichert.",

    firstNameSaved:
        "Vorname gespeichert.",

    lastNameSaved:
        "Nachname gespeichert.",

    additionalDirectorsSaved:
        "Weitere Geschäftsführer gespeichert.",

    emailAddressSaved:
        "E-Mail-Adresse gespeichert.",

    mobilePhoneNumberSaved:
        "Handynummer gespeichert.",

    streetSaved:
        "Straße gespeichert.",

    houseNoSaved:
        "Hausnummer gespeichert.",

    zipCodeSaved:
        "Postleitzahl gespeichert.",

    townSaved:
        "Ort gespeichert.",

    countrySaved:
        "Land gespeichert.",

    bankSaved:
        "Bank gespeichert.",

    bicSaved:
        "BIC gespeichert.",

    ibanSaved:
        "IBAN gespeichert.",

    vatIdSaved:
        "USt-ID gespeichert.",

    taxIdSaved:
        "Steuernummer gespeichert.",

    vatRateSaved:
        "Umsatzsteuersatz gespeichert.",

    taxExemptionSaved:
        "Umsatzsteuerstatus gespeichert.",

    registeredSaved:
        "Registerstatus gespeichert.",

    registrationCourtSaved:
        "Registergericht gespeichert.",

    registrationDataSaved:
        "Registerabteilung und Registernummer gespeichert.",

    invoicePrefixSaved:
        "Rechnungspräfix gespeichert.",

    stripeIdSaved:
        "Stripe-Konto-ID gespeichert.",

    transactionFeeSaved:
        "Transaktionsgebühr gespeichert.",

    tagsSaved:
        "Tags gespeichert.",

    usernameSaved:
        "Benutzername gespeichert.",

    passwordSaved:
        "Passwort gespeichert.",

    imprintSaved:
        "Impressum gespeichert.",

    adminRoleSaved:
        "Administratorrolle gespeichert.",

    buyerRoleSaved:
        "Käuferrolle gespeichert.",

    sellerRoleSaved:
        "Verkäuferrolle gespeichert.",

    sellerTermsSaved:
        "Verkäufer-AGB gespeichert.",

    sellerPrivacyPolicySaved:
        "Verkäufer-Datenschutzerklärung gespeichert.",

    operatorTermsSaved:
        "Operator-AGB gespeichert.",

    operatorPrivacyPolicySaved:
        "Operator-Datenschutzerklärung gespeichert.",

    disabled:
        "Partner deaktiviert.",
});

const STYLE = Object.freeze({
    page: {
        width: "100%",
    },

    headerPaper: {
        display: "block",
        width: "100%",
        overflow: "hidden",
        borderRadius: 3,
    },

    headerBar: {
        minHeight: "148px",
        padding: {
            xs: "24px",
            sm: "30px",
        },
        display: "flex",
        alignItems: "center",
        boxSizing: "border-box",
        color: "white",
    },

    headerContent: {
        width: "100%",
    },

    headerAvatar: {
        width: {
            xs: 72,
            sm: 84,
        },
        height: {
            xs: 72,
            sm: 84,
        },
        backgroundColor:
            "rgba(255,255,255,0.20)",
        border:
            "2px solid rgba(255,255,255,0.30)",
        fontSize: 30,
        fontWeight: 700,
    },

    headerTitle: {
        fontFamily:
            "Arial, Helvetica",
        fontSize: {
            xs: "1.55rem",
            sm: "1.9rem",
        },
        fontWeight: 700,
        lineHeight: 1.2,
        overflowWrap: "anywhere",
    },

    headerSubtitle: {
        marginTop: "4px",
        opacity: 0.86,
        overflowWrap: "anywhere",
    },

    headerChip: {
        color: "white",
        backgroundColor:
            "rgba(255,255,255,0.16)",
        border:
            "1px solid rgba(255,255,255,0.24)",
        fontWeight: 700,
        "& .MuiChip-icon": {
            color: "white",
        },
    },

    section: {
        display: "flex",
        flexDirection: "column",
    },

    sectionTitle: {
        display: "block",
        marginBottom: "12px",
        color: "text.secondary",
        fontWeight: 800,
        letterSpacing: "0.09em",
    },

    card: {
        display: "block",
        height: "100%",
        overflow: "hidden",
        borderRadius: 3,
        border: "1px solid",
        borderColor: alpha(
            PRIMARY_COLOR,
            0.14,
        ),
        boxShadow:
            `0 12px 30px ${alpha(
                PRIMARY_COLOR,
                0.06,
            )}`,
    },

    cardContent: {
        padding: {
            xs: "18px",
            sm: "24px",
        },
        "&:last-child": {
            paddingBottom: {
                xs: "18px",
                sm: "24px",
            },
        },
    },

    cardHeader: {
        marginBottom: "18px",
    },

    cardIcon: {
        display: "grid",
        placeItems: "center",
        flexShrink: 0,
        width: 34,
        height: 34,
        borderRadius: 2,
        backgroundColor: alpha(
            PRIMARY_COLOR,
            0.09,
        ),
        color: PRIMARY_COLOR,
    },

    cardTitle: {
        fontFamily:
            "Arial, Helvetica",
        fontSize: "1rem",
        fontWeight: 700,
        color: "text.primary",
        overflowWrap: "anywhere",
    },

    infoLabel: {
        display: "block",
        marginBottom: "3px",
        fontWeight: 600,
    },

    infoValue: {
        overflowWrap: "anywhere",
        lineHeight: 1.45,
    },

    editButton: {
        color: PRIMARY_COLOR,
    },

    kpi: {
        padding: "16px",
        borderRadius: 2.5,
        backgroundColor: alpha(
            PRIMARY_COLOR,
            0.06,
        ),
    },

    kpiValue: {
        marginTop: "4px",
        color: PRIMARY_COLOR,
        fontSize: "2rem",
        fontWeight: 800,
        lineHeight: 1,
    },

    documentRow: {
        paddingTop: "10px",
        paddingBottom: "10px",
    },

    documentButton: {
        alignSelf: {
            xs: "stretch",
            sm: "center",
        },
        borderRadius: 2,
    },

    imprint: {
        margin: 0,
        whiteSpace: "pre-wrap",
        overflowWrap: "anywhere",
        fontFamily: "inherit",
        lineHeight: 1.65,
    },

    dialogTitle: {
        fontFamily:
            "Arial, Helvetica",
        fontWeight: 700,
    },
});

const EDITOR_DIALOG_Z_INDEX = 100100;

const displayValue = (
    value,
) => {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return EMPTY_VALUE;
    }

    return String(value);
};

const formatImprint = (
    value,
) => {
    return toEmptyStringIfNull(
        value,
    ).replaceAll(
        "$",
        "\n",
    );
};

const preprocessImprint = (
    value,
) => {
    return toEmptyStringIfNull(
        value,
    ).replaceAll(
        "\n",
        "$",
    );
};

const formatEnumValue = (
    value,
) => {
    if (!value) {
        return EMPTY_VALUE;
    }

    return String(value)
        .toLowerCase()
        .split("_")
        .map((part) => {
            return (
                part
                    .charAt(0)
                    .toUpperCase()
                + part.slice(1)
            );
        })
        .join(" ");
};

const extractEnumValues = (
    response,
    possibleKeys = [],
) => {
    const candidates = [
        response,
        response?.data,
        response?.content,
        response?.values,
        response?.result,

        ...possibleKeys.map((key) => {
            return response?.[key];
        }),

        ...possibleKeys.map((key) => {
            return response?.data?.[key];
        }),

        ...possibleKeys.map((key) => {
            return response?.result?.[key];
        }),
    ];

    const directArray =
        candidates.find(Array.isArray);

    if (directArray) {
        return directArray;
    }

    /*
     * Fallback für unbekannte Response-Strukturen wie:
     *
     * {
     *     irgendeinName: [...]
     * }
     */
    if (
        response
        && typeof response === "object"
        && !Array.isArray(response)
    ) {
        const nestedArray =
            Object.values(response)
                .find(Array.isArray);

        if (nestedArray) {
            return nestedArray;
        }
    }

    return [];
};

const mapEnumOptions = (
    values,
) => {

    if (!Array.isArray(values)) {
        return [];
    }

    return values
        .map((item) => {
            if (
                item === null
                || item === undefined
            ) {
                return null;
            }

            /*
             * Unterstützt einfache Enum-Strings:
             *
             * [
             *     "AMTSGERICHT_STUTTGART",
             *     "AMTSGERICHT_MUENCHEN"
             * ]
             */
            if (
                typeof item !== "object"
            ) {
                const value =
                    String(item).trim();

                if (!value) {
                    return null;
                }

                const label =
                    /^[A-Z0-9]{2,5}$/
                        .test(value)
                        ? value
                        : formatEnumValue(value);

                return {
                    value,
                    label,
                };
            }

            /*
             * Unterstützt auch Objekt-Responses:
             *
             * {
             *     value: "HRB",
             *     label: "HRB"
             * }
             *
             * oder:
             *
             * {
             *     code: "HRB",
             *     description: "Handelsregister B"
             * }
             */
            const rawValue =
                item.value
                ?? item.code
                ?? item.name
                ?? item.id
                ?? item.key;

            if (
                rawValue === null
                || rawValue === undefined
                || String(rawValue).trim() === ""
            ) {
                return null;
            }

            const value =
                String(rawValue).trim();

            const rawLabel =
                item.label
                ?? item.description
                ?? item.displayName
                ?? item.title;

            const label =
                rawLabel !== null
                && rawLabel !== undefined
                && String(rawLabel).trim() !== ""
                    ? String(rawLabel).trim()
                    : (
                        /^[A-Z0-9]{2,5}$/
                            .test(value)
                            ? value
                            : formatEnumValue(value)
                     );

            return {
                value,
                label,
            };
        })
        .filter(Boolean);
};

const normalizeRegistrationCourt = (
    value,
) => {
    if (!value) {
        return "";
    }

    return (
        REGISTRATION_COURT_ALIASES[
            String(value)
        ]
        ?? String(value)
    );
};

const formatRegistrationCourt = (
    value,
    options,
) => {
    const normalizedValue =
        normalizeRegistrationCourt(
            value,
        );

    if (!normalizedValue) {
        return EMPTY_VALUE;
    }

    const option =
        options.find(
            (item) => {
                return (
                    item.value
                    === normalizedValue
                );
            },
        );

    return (
        option?.label
        ?? formatEnumValue(
            normalizedValue,
        )
    );
};

const formatPercentage = (
    value,
) => {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return EMPTY_VALUE;
    }

    return `${value} %`;
};

const formatRegistrationNumber = (
    section,
    number,
) => {
    const value = [
        section,
        number,
    ]
        .filter(Boolean)
        .join(" ");

    return value || EMPTY_VALUE;
};

const maskValue = (
    value,
    visible,
    visibleCharacters = 4,
) => {
    if (
        visible
        || value === null
        || value === undefined
        || value === ""
    ) {
        return value;
    }

    const normalized =
        String(value)
            .replaceAll(
                " ",
                "",
            );

    const suffix =
        normalized.slice(
            -visibleCharacters,
        );

    return suffix
        ? `••••••••${suffix}`
        : "••••••••";
};

const maskIban = (
    value,
    visible,
) => {
    if (
        visible
        || value === null
        || value === undefined
        || value === ""
    ) {
        return value;
    }

    const normalized =
        String(value)
            .replaceAll(
                " ",
                "",
            );

    if (normalized.length <= 6) {
        return maskValue(
            normalized,
            false,
            4,
        );
    }

    const masked =
        `${normalized.slice(0, 2)}`
        + `${"•".repeat(
            Math.max(
                0,
                normalized.length - 6,
            ),
        )}`
        + `${normalized.slice(-4)}`;

    return (
        masked.match(/.{1,4}/g)
            ?.join(" ")
        ?? masked
    );
};

const getTagId = (
    tag,
) => {
    return (
        tag?.id
        ?? tag?.tagId
        ?? null
    );
};

const getTagLabel = (
    tag,
) => {
    return (
        tag?.name
        ?? tag?.label
        ?? tag?.value
        ?? tag?.description
        ?? (
            getTagId(tag) !== null
                ? `Tag ${getTagId(tag)}`
                : "Tag"
        )
    );
};

const hasBinaryData = (
    value,
) => {
    if (
        typeof value === "string"
    ) {
        return value.trim().length > 0;
    }

    if (
        value instanceof Uint8Array
    ) {
        return value.length > 0;
    }

    return (
        Array.isArray(value)
        && value.length > 0
    );
};

const isPdfFile = (
    file,
) => {
    if (!file) {
        return false;
    }

    return (
        file.type
            === "application/pdf"
        || file.name
            ?.toLowerCase()
            .endsWith(".pdf")
    );
};

const base64ToBlob = (
    base64,
    mimeType,
) => {
    const normalized =
        base64.includes(",")
            ? base64.substring(
                base64.indexOf(",") + 1,
            )
            : base64;

    const binary =
        window.atob(
            normalized,
        );

    const bytes =
        new Uint8Array(
            binary.length,
        );

    for (
        let index = 0;
        index < binary.length;
        index += 1
    ) {
        bytes[index] =
            binary.charCodeAt(
                index,
            );
    }

    return new Blob(
        [bytes],
        {
            type: mimeType,
        },
    );
};

const downloadPdf = (
    data,
    fileName,
) => {
    if (!hasBinaryData(data)) {
        return;
    }

    const blob =
        typeof data === "string"
            ? base64ToBlob(
                data,
                "application/pdf",
            )
            : new Blob(
                [
                    data instanceof Uint8Array
                        ? data
                        : new Uint8Array(
                            data,
                        ),
                ],
                {
                    type:
                        "application/pdf",
                },
            );

    const url =
        window.URL
            .createObjectURL(
                blob,
            );

    const anchor =
        document.createElement(
            "a",
        );

    anchor.href = url;
    anchor.download = fileName;

    document.body
        .appendChild(
            anchor,
        );

    anchor.click();
    anchor.remove();

    window.URL
        .revokeObjectURL(
            url,
        );
};

function EditorSection({
    title,
    children,
}) {
    return (
        <Box
            component="section"
            sx={STYLE.section}
        >
            <Typography
                variant="overline"
                sx={STYLE.sectionTitle}
            >
                {title}
            </Typography>

            {children}
        </Box>
    );
}

function OverviewCard({
    title,
    icon,
    action = null,
    children,
}) {
    return (
        <Paper
            variant="outlined"
            sx={STYLE.card}
        >
            <CardContent
                sx={STYLE.cardContent}
            >
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                    sx={STYLE.cardHeader}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1.25}
                        sx={{
                            minWidth: 0,
                        }}
                    >
                        <Box
                            sx={STYLE.cardIcon}
                        >
                            {icon}
                        </Box>

                        <Typography
                            variant="h6"
                            sx={STYLE.cardTitle}
                        >
                            {title}
                        </Typography>
                    </Stack>

                    {action}
                </Stack>

                {children}
            </CardContent>
        </Paper>
    );
}

function InfoRow({
    label,
    value,
    onEdit,
    editable = false,
    monospace = false,
}) {
    return (
        <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={1}
        >
            <Box
                sx={{
                    minWidth: 0,
                    flexGrow: 1,
                }}
            >
                <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={STYLE.infoLabel}
                >
                    {label}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        ...STYLE.infoValue,
                        fontFamily:
                            monospace
                                ? "monospace"
                                : "inherit",
                    }}
                >
                    {displayValue(value)}
                </Typography>
            </Box>

            {editable && onEdit && (
                <Tooltip
                    title={
                        `${LABEL.edit}: ${label}`
                    }
                >
                    <IconButton
                        size="small"
                        onClick={onEdit}
                        aria-label={
                            `${LABEL.edit}: ${label}`
                        }
                        sx={STYLE.editButton}
                    >
                        <EditOutlinedIcon
                            fontSize="small"
                        />
                    </IconButton>
                </Tooltip>
            )}
        </Stack>
    );
}

function TransactionFeeKpi({
    value,
    editable,
    onEdit,
}) {
    return (
        <Box sx={STYLE.kpi}>
            <Stack
                direction="row"
                alignItems="flex-start"
                justifyContent="space-between"
                spacing={1}
            >
                <Box>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={STYLE.infoLabel}
                    >
                        {LABEL.transactionFee}
                    </Typography>

                    <Typography
                        component="div"
                        sx={STYLE.kpiValue}
                    >
                        {
                            formatPercentage(
                                value,
                            )
                        }
                    </Typography>
                </Box>

                {editable && (
                    <Tooltip
                        title={
                            `${LABEL.edit}: ${LABEL.transactionFee}`
                        }
                    >
                        <IconButton
                            size="small"
                            onClick={onEdit}
                            sx={STYLE.editButton}
                        >
                            <EditOutlinedIcon
                                fontSize="small"
                            />
                        </IconButton>
                    </Tooltip>
                )}
            </Stack>
        </Box>
    );
}

function StatusChip({
    value,
    positiveLabel,
    negativeLabel,
}) {
    if (value === null) {
        return (
            <Chip
                size="small"
                label={
                    LABEL.statusUnknown
                }
                variant="outlined"
            />
        );
    }

    return (
        <Chip
            size="small"
            label={
                value
                    ? positiveLabel
                    : negativeLabel
            }
            color={
                value
                    ? "success"
                    : "default"
            }
            variant={
                value
                    ? "filled"
                    : "outlined"
            }
        />
    );
}

/*
 * Für Verkäufer- und Operatordokumente wird dieselbe
 * Darstellung und dieselbe Verfügbarkeitslogik verwendet.
 */
function DocumentRow({
    title,
    data,
    fileName,
    canUpload,
    busy,
    onUpload,
}) {
    const available =
        hasBinaryData(data);

    const handleFile = (
        event,
    ) => {
        const [file] =
            event.target.files
            ?? [];

        event.target.value = "";

        if (file) {
            onUpload(file);
        }
    };

    return (
        <Stack
            direction={{
                xs: "column",
                sm: "row",
            }}
            alignItems={{
                xs: "stretch",
                sm: "center",
            }}
            justifyContent="space-between"
            spacing={2}
            sx={STYLE.documentRow}
        >
            <Box
                sx={{
                    minWidth: 0,
                }}
            >
                <Typography
                    fontWeight={700}
                >
                    {title}
                </Typography>

                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {
                        available
                            ? LABEL.documentStored
                            : LABEL.documentMissing
                    }
                </Typography>
            </Box>

            <Stack
                direction="row"
                spacing={1}
                useFlexGap
                flexWrap="wrap"
            >
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!available}
                    sx={STYLE.documentButton}
                    startIcon={
                        <DownloadOutlinedIcon />
                    }
                    onClick={() => {
                        downloadPdf(
                            data,
                            fileName,
                        );
                    }}
                >
                    {LABEL.download}
                </Button>

                {canUpload && (
                    <Button
                        component="label"
                        size="small"
                        variant="outlined"
                        disabled={busy}
                        sx={STYLE.documentButton}
                        startIcon={
                            busy
                                ? (
                                    <CircularProgress
                                        size={16}
                                        color="inherit"
                                    />
                                )
                                : (
                                    <UploadFileOutlinedIcon />
                                )
                        }
                    >
                        {
                            available
                                ? LABEL.replace
                                : LABEL.upload
                        }

                        <input
                            hidden
                            type="file"
                            accept="application/pdf,.pdf"
                            onChange={
                                handleFile
                            }
                        />
                    </Button>
                )}
            </Stack>
        </Stack>
    );
}

function EditValueDialog({
    open,
    title,
    label,
    initialValue,
    inputMode,
    validate,
    onClose,
    onSave,
}) {
    const [
        value,
        setValue,
    ] = useState("");

    const [
        error,
        setError,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(
                initialValue
                ?? "",
            );

            setError("");
            setSaving(false);
        }
    }, [
        initialValue,
        open,
    ]);

    const save = async () => {
        setError("");

        if (validate) {
            const validationMessage =
                await validate(
                    value,
                );

            if (validationMessage) {
                setError(
                    validationMessage,
                );

                return;
            }
        }

        setSaving(true);

        try {
            const saved =
                await onSave(
                    value,
                );

            if (saved) {
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
                        sx={{
                zIndex:
                    EDITOR_DIALOG_Z_INDEX,

                
            }}
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={STYLE.dialogTitle}>
                {title}
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    autoFocus
                    fullWidth
                    value={value}
                    label={label}
                    inputProps={
                        inputMode
                            ? {
                                inputMode,
                            }
                            : undefined
                    }
                    error={
                        Boolean(error)
                    }
                    helperText={
                        error || " "
                    }
                    onChange={(
                        event,
                    ) => {
                        setValue(
                            event.target
                                .value,
                        );
                    }}
                    onKeyDown={(
                        event,
                    ) => {
                        if (
                            event.key
                            === "Enter"
                        ) {
                            event
                                .preventDefault();

                            void save();
                        }
                    }}
                />
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    {LABEL.close}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        void save();
                    }}
                    disabled={saving}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {LABEL.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function SelectValueDialog({
    open,
    title,
    label,
    initialValue,
    options = [],
    loading = false,
    emptyMessage =
        "Keine Auswahlmöglichkeiten verfügbar.",
    onClose,
    onSave,
}) {
    const [
        value,
        setValue,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    useEffect(() => {
        if (open) {
            setValue(
                initialValue
                ?? "",
            );

            setSaving(false);
        }
    }, [
        initialValue,
        open,
    ]);

    const save = async () => {
        setSaving(true);

        try {
            const saved =
                await onSave(
                    value,
                );

            if (saved) {
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    const hasOptions =
        options.length > 0;

    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={STYLE.dialogTitle}>
                {title}
            </DialogTitle>

            <DialogContent dividers>
                <TextField
                    select
                    autoFocus
                    fullWidth
                    value={value}
                    label={label}
                    disabled={
                        loading
                        || !hasOptions
                    }
                    helperText={
                        loading
                            ? "Wird geladen …"
                            : hasOptions
                                ? " "
                                : emptyMessage
                    }
                    onChange={(
                        event,
                    ) => {
                        setValue(
                            event.target
                                .value,
                        );
                    }}
                >
                    {options.map(
                        (option) => (
                            <MenuItem
                                key={
                                    option.value
                                }
                                value={
                                    option.value
                                }
                            >
                                {
                                    option.label
                                }
                            </MenuItem>
                        ),
                    )}
                </TextField>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    {LABEL.close}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        void save();
                    }}
                    disabled={
                        saving
                        || loading
                        || !hasOptions
                        || value === ""
                    }
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {LABEL.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function RegistrationDataDialog({
    open,
    initialSection,
    initialNumber,
    sections = [],
    sectionsLoading = false,
    validateNumber,
    onClose,
    onSave,
}) {
    const [
        section,
        setSection,
    ] = useState("");

    const [
        number,
        setNumber,
    ] = useState("");

    const [
        error,
        setError,
    ] = useState("");

    const [
        saving,
        setSaving,
    ] = useState(false);

    useEffect(() => {
        if (open) {
            setSection(
                initialSection
                ?? "",
            );

            setNumber(
                initialNumber
                ?? "",
            );

            setError("");
            setSaving(false);
        }
    }, [
        initialNumber,
        initialSection,
        open,
    ]);

    const save = async () => {
        setError("");

        const validationMessage =
            await validateNumber(
                number,
            );

        if (validationMessage) {
            setError(
                validationMessage,
            );

            return;
        }

        setSaving(true);

        try {
            const saved =
                await onSave(
                    section,
                    number,
                );

            if (saved) {
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={STYLE.dialogTitle}>
                Registerdaten ändern
            </DialogTitle>

            <DialogContent dividers>
                <Stack spacing={2}>
                    <TextField
                        select
                        fullWidth
                        value={section}
                        label={
                            LABEL.registrationSection
                        }
                        disabled={
                            sectionsLoading
                            || sections.length === 0
                        }
                        helperText={
                            sectionsLoading
                                ? "Wird geladen …"
                                : sections.length > 0
                                    ? " "
                                    : "Keine Registerabteilungen verfügbar."
                        }
                        onChange={(
                            event,
                        ) => {
                            setSection(
                                event.target
                                    .value,
                            );
                        }}
                    >
                        {
                            sections
                                .map(
                                    (option) => (
                                        <MenuItem
                                            key={
                                                option.value
                                            }
                                            value={
                                                option.value
                                            }
                                        >
                                            {
                                                option.label
                                            }
                                        </MenuItem>
                                    ),
                                )
                        }
                    </TextField>

                    <TextField
                        fullWidth
                        value={number}
                        label={
                            LABEL.registrationNo
                        }
                        error={
                            Boolean(error)
                        }
                        helperText={
                            error || " "
                        }
                        onChange={(
                            event,
                        ) => {
                            setNumber(
                                event.target
                                    .value,
                            );
                        }}
                    />
                </Stack>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    {LABEL.close}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        void save();
                    }}
                    disabled={
                        saving
                        || sectionsLoading
                        || sections.length === 0
                        || section === ""
                    }
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {LABEL.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function TagsDialog({
    open,
    tags,
    selectedTags,
    onClose,
    onSave,
}) {
    const [
        selectedIds,
        setSelectedIds,
    ] = useState([]);

    const [
        saving,
        setSaving,
    ] = useState(false);

    useEffect(() => {
        if (open) {
            setSelectedIds(
                selectedTags
                    .map(getTagId)
                    .filter((id) => {
                        return id !== null;
                    })
                    .map(String),
            );

            setSaving(false);
        }
    }, [
        open,
        selectedTags,
    ]);

    const validTags =
        tags.filter((tag) => {
            return (
                getTagId(tag)
                !== null
            );
        });

    const save = async () => {
        const normalizedIds =
            selectedIds
                .map(Number)
                .filter(
                    Number.isSafeInteger,
                );

        setSaving(true);

        try {
            const saved =
                await onSave(
                    normalizedIds,
                );

            if (saved) {
                onClose();
            }
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={
                saving
                    ? undefined
                    : onClose
            }
            fullWidth
            maxWidth="sm"
        >
            <DialogTitle sx={STYLE.dialogTitle}>
                {LABEL.tags}
            </DialogTitle>

            <DialogContent dividers>
                <FormControl fullWidth>
                    <InputLabel
                        id="user-tags-label"
                    >
                        {LABEL.tags}
                    </InputLabel>

                    <Select
                        labelId="user-tags-label"
                        multiple
                        value={
                            selectedIds
                        }
                        input={
                            <OutlinedInput
                                label={
                                    LABEL.tags
                                }
                            />
                        }
                        renderValue={(
                            values,
                        ) => {
                            return values
                                .map((id) => {
                                    const tag =
                                        validTags
                                            .find(
                                                (
                                                    item,
                                                ) => {
                                                    return (
                                                        String(
                                                            getTagId(
                                                                item,
                                                            ),
                                                        )
                                                        === String(
                                                            id,
                                                        )
                                                    );
                                                },
                                            );

                                    return tag
                                        ? getTagLabel(
                                            tag,
                                        )
                                        : id;
                                })
                                .join(", ");
                        }}
                        onChange={(
                            event,
                        ) => {
                            const value =
                                event.target
                                    .value;

                            setSelectedIds(
                                typeof value
                                    === "string"
                                    ? value
                                        .split(",")
                                    : value,
                            );
                        }}
                    >
                        {validTags.map(
                            (tag) => {
                                const id =
                                    String(
                                        getTagId(
                                            tag,
                                        ),
                                    );

                                return (
                                    <MenuItem
                                        key={id}
                                        value={id}
                                    >
                                        <Checkbox
                                            checked={
                                                selectedIds
                                                    .includes(
                                                        id,
                                                    )
                                            }
                                        />

                                        <ListItemText
                                            primary={
                                                getTagLabel(
                                                    tag,
                                                )
                                            }
                                        />
                                    </MenuItem>
                                );
                            },
                        )}
                    </Select>
                </FormControl>
            </DialogContent>

            <DialogActions>
                <Button
                    onClick={onClose}
                    disabled={saving}
                >
                    {LABEL.close}
                </Button>

                <Button
                    variant="contained"
                    onClick={() => {
                        void save();
                    }}
                    disabled={saving}
                    startIcon={
                        saving
                            ? (
                                <CircularProgress
                                    size={18}
                                    color="inherit"
                                />
                            )
                            : undefined
                    }
                >
                    {LABEL.save}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

function FeatureDialog({
    open,
    title,
    onClose,
    children,
    maxWidth = "md",
}) {
    return (
        <Dialog
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth={maxWidth}
        >
            <DialogTitle>
                <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    spacing={2}
                >
                    <Typography
                        variant="h6"
                        component="div"
                        sx={STYLE.dialogTitle}
                    >
                        {title}
                    </Typography>

                    <IconButton
                        onClick={onClose}
                        aria-label={
                            LABEL.close
                        }
                    >
                        <CloseOutlinedIcon />
                    </IconButton>
                </Stack>
            </DialogTitle>

            <DialogContent dividers>
                {children}
            </DialogContent>
        </Dialog>
    );
}

function UserEditorSkeleton() {
    return (
        <Stack spacing={4}>
            <Skeleton
                variant="rounded"
                height={148}
                sx={{
                    borderRadius: 3,
                }}
            />

            {[0, 1, 2].map(
                (section) => (
                    <Box key={section}>
                        <Skeleton
                            variant="text"
                            width={220}
                            height={24}
                        />

                        <Grid
                            container
                            spacing={3}
                            sx={{
                                mt: 0,
                            }}
                        >
                            {[0, 1, 2].map(
                                (card) => (
                                    <Grid
                                        item
                                        xs={12}
                                        md={4}
                                        key={card}
                                    >
                                        <Skeleton
                                            variant="rounded"
                                            height={240}
                                            sx={{
                                                borderRadius: 3,
                                            }}
                                        />
                                    </Grid>
                                ),
                            )}
                        </Grid>
                    </Box>
                ),
            )}
        </Stack>
    );
}

const PARTNER_MENU_PROPS = Object.freeze({
    PaperProps: {
        style: {
            maxHeight: 48 * 6.5 + 8,
            width: 320,
        },
    },
});

const PARTNER_LABEL = Object.freeze({
    title: "Partnermanagement",
    description:
        "Hier können Partner für Geschäfte mit diesem Verkäufer gesperrt oder wieder freigegeben werden.",
    blockedPartners: "Gesperrte Partner",
    noPartners: "Keine Partner verfügbar",
    noBlockedPartners: "Keine Partner gesperrt",
    loading: "Partner werden geladen …",
    saving: "Änderungen werden gespeichert …",
    reload: "Partner neu laden",
    loadError: "Die Partnerdaten konnten nicht geladen werden.",
    partialLoadError:
        "Einige Partnerprofile konnten nicht vollständig geladen werden.",
    saveError:
        "Die Änderungen konnten nicht vollständig gespeichert werden.",
    changesSaved: "Partnermanagement gespeichert.",
    accessTokenMissing: "Access Token fehlt.",
    partnerFallback: "Partner",
    partnerId: "Partner-ID",
});

function partnerRequestToPromise(request) {
    return new Promise((resolve, reject) => {
        handleResponse(
            request,
            resolve,
            reject,
        );
    });
}

function normalizePartnerId(value) {
    if (
        value === null
        || value === undefined
        || value === ""
    ) {
        return null;
    }

    return String(value);
}

function extractPartnerId(value) {
    if (
        value
        && typeof value === "object"
        && !Array.isArray(value)
    ) {
        return normalizePartnerId(value.id);
    }

    return normalizePartnerId(value);
}

function normalizePartnerIdList(values) {
    if (!Array.isArray(values)) {
        return [];
    }

    return [
        ...new Set(
            values
                .map(extractPartnerId)
                .filter(Boolean),
        ),
    ];
}

function normalizePartner(partner, fallbackId) {
    const source =
        partner
        && typeof partner === "object"
        && !Array.isArray(partner)
            ? partner
            : {};

    return {
        ...source,
        id: normalizePartnerId(
            source.id ?? fallbackId,
        ),
    };
}

function formatPartnerName(partner) {
    const fullName = [
        partner.firstName,
        partner.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    const businessName =
        partner.businessName || "";

    if (fullName && businessName) {
        return `${fullName} · ${businessName}`;
    }

    if (businessName) {
        return businessName;
    }

    if (fullName) {
        return fullName;
    }

    if (partner.username) {
        return partner.username;
    }

    return `${PARTNER_LABEL.partnerFallback} ${partner.id}`;
}

function comparePartners(left, right) {
    return formatPartnerName(left).localeCompare(
        formatPartnerName(right),
        "de",
        {
            sensitivity: "base",
        },
    );
}

function PartnerManagement({
    appAPI,
    contextAPI,
    userId,
}) {
    const [partners, setPartners] = useState([]);
    const [
        blockedPartnerIds,
        setBlockedPartnerIds,
    ] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [loadFailed, setLoadFailed] = useState(false);
    const [
        profileLoadFailed,
        setProfileLoadFailed,
    ] = useState(false);

    const showMessageRef = useRef(
        appAPI.showMessage,
    );

    useEffect(() => {
        showMessageRef.current =
            appAPI.showMessage;
    }, [appAPI.showMessage]);

    const accessToken = contextAPI.password;

    const normalizedUserId = useMemo(
        () => normalizePartnerId(userId),
        [userId],
    );

    const partnerById = useMemo(
        () =>
            new Map(
                partners.map((partner) => [
                    partner.id,
                    partner,
                ]),
            ),
        [partners],
    );

    const loadPartnerData = useCallback(
        async () => {
            if (
                typeof accessToken !== "string"
                || accessToken.trim().length === 0
            ) {
                setLoading(false);
                setLoadFailed(true);

                showMessageRef.current(
                    PARTNER_LABEL.accessTokenMissing,
                );

                return;
            }

            setLoading(true);
            setLoadFailed(false);
            setProfileLoadFailed(false);

            try {
                const [
                    buyersResponse,
                    blockedBuyersResponse,
                ] = await Promise.all([
                    partnerRequestToPromise(
                        fetchBuyersAPI(
                            accessToken,
                        ),
                    ),
                    partnerRequestToPromise(
                        fetchBlockedBuyersAPI(
                            accessToken,
                            userId,
                        ),
                    ),
                ]);

                const buyerIds =
                    normalizePartnerIdList(
                        buyersResponse,
                    ).filter(
                        (buyerId) =>
                            buyerId
                            !== normalizedUserId,
                    );

                const blockedIds =
                    normalizePartnerIdList(
                        blockedBuyersResponse,
                    ).filter(
                        (buyerId) =>
                            buyerId
                            !== normalizedUserId,
                    );

                const embeddedPartners =
                    new Map();

                if (Array.isArray(buyersResponse)) {
                    buyersResponse.forEach(
                        (buyer) => {
                            if (
                                buyer
                                && typeof buyer
                                    === "object"
                                && !Array.isArray(buyer)
                            ) {
                                const buyerId =
                                    extractPartnerId(
                                        buyer,
                                    );

                                if (
                                    buyerId
                                    && buyerId
                                        !== normalizedUserId
                                ) {
                                    embeddedPartners.set(
                                        buyerId,
                                        normalizePartner(
                                            buyer,
                                            buyerId,
                                        ),
                                    );
                                }
                            }
                        },
                    );
                }

                const profileResults =
                    await Promise.allSettled(
                        buyerIds.map(
                            async (buyerId) => {
                                if (
                                    embeddedPartners.has(
                                        buyerId,
                                    )
                                ) {
                                    return embeddedPartners.get(
                                        buyerId,
                                    );
                                }

                                const profile =
                                    await partnerRequestToPromise(
                                        fetchUserProfileAPI(
                                            buyerId,
                                            accessToken,
                                        ),
                                    );

                                return normalizePartner(
                                    profile,
                                    buyerId,
                                );
                            },
                        ),
                    );

                const loadedPartners =
                    profileResults.map(
                        (result, index) => {
                            if (
                                result.status
                                === "fulfilled"
                            ) {
                                return result.value;
                            }

                            return normalizePartner(
                                null,
                                buyerIds[index],
                            );
                        },
                    );

                setPartners(
                    loadedPartners
                        .filter(
                            (partner) =>
                                partner.id
                                !== normalizedUserId,
                        )
                        .sort(comparePartners),
                );

                setBlockedPartnerIds(
                    blockedIds,
                );

                setProfileLoadFailed(
                    profileResults.some(
                        (result) =>
                            result.status
                            === "rejected",
                    ),
                );
            } catch (error) {
                console.error(
                    PARTNER_LABEL.loadError,
                    error,
                );

                setLoadFailed(true);

                showMessageRef.current(
                    error?.message
                    || String(error)
                    || PARTNER_LABEL.loadError,
                );
            } finally {
                setLoading(false);
            }
        },
        [
            accessToken,
            normalizedUserId,
            userId,
        ],
    );

    useEffect(() => {
        loadPartnerData();
    }, [loadPartnerData]);

    const handlePartnerChange = async (event) => {
        const rawValue = event.target.value;

        const nextBlockedIds =
            normalizePartnerIdList(
                typeof rawValue === "string"
                    ? rawValue.split(",")
                    : rawValue,
            ).filter(
                (partnerId) =>
                    partnerId
                    !== normalizedUserId,
            );

        const previousBlockedIds =
            blockedPartnerIds.filter(
                (partnerId) =>
                    partnerId
                    !== normalizedUserId,
            );

        const previousIdSet = new Set(
            previousBlockedIds,
        );
        const nextIdSet = new Set(
            nextBlockedIds,
        );

        const addedIds =
            nextBlockedIds.filter(
                (partnerId) =>
                    !previousIdSet.has(partnerId),
            );
        const removedIds =
            previousBlockedIds.filter(
                (partnerId) =>
                    !nextIdSet.has(partnerId),
            );

        if (
            addedIds.length === 0
            && removedIds.length === 0
        ) {
            setBlockedPartnerIds(
                nextBlockedIds,
            );

            return;
        }

        setBlockedPartnerIds(nextBlockedIds);
        setSaving(true);

        try {
            const operations = [
                ...addedIds.map(
                    (buyerId) =>
                        partnerRequestToPromise(
                            blockBuyerAPI(
                                accessToken,
                                userId,
                                buyerId,
                            ),
                        ),
                ),
                ...removedIds.map(
                    (buyerId) =>
                        partnerRequestToPromise(
                            unblockBuyerAPI(
                                accessToken,
                                userId,
                                buyerId,
                            ),
                        ),
                ),
            ];

            const results =
                await Promise.allSettled(
                    operations,
                );

            if (
                results.some(
                    (result) =>
                        result.status
                        === "rejected",
                )
            ) {
                showMessageRef.current(
                    PARTNER_LABEL.saveError,
                );

                await loadPartnerData();

                return;
            }

            showMessageRef.current(
                PARTNER_LABEL.changesSaved,
            );
        } catch (error) {
            console.error(
                PARTNER_LABEL.saveError,
                error,
            );

            setBlockedPartnerIds(
                previousBlockedIds,
            );

            showMessageRef.current(
                error?.message
                || PARTNER_LABEL.saveError,
            );
        } finally {
            setSaving(false);
        }
    };

    const renderSelectedPartners = (
        selectedIds,
    ) => {
        const filteredSelectedIds =
            normalizePartnerIdList(
                selectedIds,
            ).filter(
                (partnerId) =>
                    partnerId
                    !== normalizedUserId,
            );

        if (filteredSelectedIds.length === 0) {
            return (
                <Typography
                    variant="body2"
                    color="text.secondary"
                >
                    {
                        PARTNER_LABEL.noBlockedPartners
                    }
                </Typography>
            );
        }

        return (
            <Box
                sx={{
                    display: "flex",
                    gap: 0.75,
                    flexWrap: "wrap",
                }}
            >
                {filteredSelectedIds.map(
                    (partnerId) => {
                        const partner =
                            partnerById.get(
                                partnerId,
                            );

                        return (
                            <Chip
                                key={partnerId}
                                size="small"
                                label={
                                    partner
                                        ? formatPartnerName(
                                            partner,
                                        )
                                        : `${PARTNER_LABEL.partnerFallback} ${partnerId}`
                                }
                            />
                        );
                    },
                )}
            </Box>
        );
    };

    return (
        <Card
            variant="outlined"
            sx={{
                borderRadius: 2,
                overflow: "hidden",
            }}
        >
            <Box
                sx={{
                    px: 2.5,
                    py: 2,
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                }}
            >
                <Avatar
                    sx={{
                        width: 38,
                        height: 38,
                        bgcolor: alpha(
                            PRIMARY_COLOR,
                            0.1,
                        ),
                        color: PRIMARY_COLOR,
                    }}
                >
                    <BlockOutlinedIcon fontSize="small" />
                </Avatar>

                <Box
                    sx={{
                        flexGrow: 1,
                        minWidth: 0,
                    }}
                >
                    <Typography variant="h6">
                        {PARTNER_LABEL.title}
                    </Typography>

                    <Typography
                        variant="body2"
                        color="text.secondary"
                    >
                        {PARTNER_LABEL.description}
                    </Typography>
                </Box>

                <Tooltip title={PARTNER_LABEL.reload}>
                    <span>
                        <IconButton
                            onClick={loadPartnerData}
                            disabled={
                                loading
                                || saving
                            }
                            aria-label={
                                PARTNER_LABEL.reload
                            }
                            sx={{
                                color: PRIMARY_COLOR,
                            }}
                        >
                            <RefreshOutlinedIcon />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            <Divider />

            <CardContent>
                <Stack spacing={2}>
                    {loadFailed && (
                        <Alert severity="error">
                            {PARTNER_LABEL.loadError}
                        </Alert>
                    )}

                    {profileLoadFailed && (
                        <Alert severity="warning">
                            {
                                PARTNER_LABEL.partialLoadError
                            }
                        </Alert>
                    )}

                    <FormControl
                        fullWidth
                        disabled={
                            loading
                            || saving
                            || loadFailed
                        }
                    >
                        <InputLabel
                            id="blocked-partners-label"
                            shrink
                        >
                            {
                                PARTNER_LABEL.blockedPartners
                            }
                        </InputLabel>

                        <Select
                            labelId="blocked-partners-label"
                            multiple
                            displayEmpty
                            value={blockedPartnerIds}
                            onChange={
                                handlePartnerChange
                            }
                            input={
                                <OutlinedInput
                                    label={
                                        PARTNER_LABEL.blockedPartners
                                    }
                                />
                            }
                            renderValue={
                                renderSelectedPartners
                            }
                            MenuProps={
                                PARTNER_MENU_PROPS
                            }
                        >
                            {partners.length === 0 ? (
                                <MenuItem disabled>
                                    <ListItemText
                                        primary={
                                            PARTNER_LABEL.noPartners
                                        }
                                    />
                                </MenuItem>
                            ) : (
                                partners.map(
                                    (partner) => (
                                        <MenuItem
                                            key={partner.id}
                                            value={partner.id}
                                        >
                                            <Checkbox
                                                checked={
                                                    blockedPartnerIds.includes(
                                                        partner.id,
                                                    )
                                                }
                                            />

                                            <ListItemText
                                                primary={
                                                    formatPartnerName(
                                                        partner,
                                                    )
                                                }
                                                secondary={
                                                    `${PARTNER_LABEL.partnerId}: ${partner.id}`
                                                }
                                            />
                                        </MenuItem>
                                    ),
                                )
                            )}
                        </Select>

                        <FormHelperText>
                            {saving
                                ? PARTNER_LABEL.saving
                                : PARTNER_LABEL.description}
                        </FormHelperText>
                    </FormControl>

                    {(loading || saving) && (
                        <Stack
                            direction="row"
                            spacing={1.5}
                            alignItems="center"
                        >
                            <CircularProgress
                                size={20}
                            />

                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {saving
                                    ? PARTNER_LABEL.saving
                                    : PARTNER_LABEL.loading}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </CardContent>
        </Card>
    );
}

export default function UserEditor({
    appAPI,
    contextAPI,
    userId,
}) {
    const [
        user,
        setUser,
    ] = useState(null);

    const [
        canBuy,
        setCanBuy,
    ] = useState(null);

    const [
        canSell,
        setCanSell,
    ] = useState(null);

    const [
        availableTags,
        setAvailableTags,
    ] = useState([]);

    const [
        registrationCourtOptions,
        setRegistrationCourtOptions,
    ] = useState([]);

    const [
        registrationSectionOptions,
        setRegistrationSectionOptions,
    ] = useState([]);

    const [
        registrationOptionsLoading,
        setRegistrationOptionsLoading,
    ] = useState(false);

    const [
        loading,
        setLoading,
    ] = useState(true);

    const [
        refreshing,
        setRefreshing,
    ] = useState(false);

    const [
        loadFailed,
        setLoadFailed,
    ] = useState(false);

    const [
        refreshFailed,
        setRefreshFailed,
    ] = useState(false);

    const [
        reloadVersion,
        setReloadVersion,
    ] = useState(0);

    const [
        openDialog,
        setOpenDialog,
    ] = useState(null);

    const [
        busyAction,
        setBusyAction,
    ] = useState(null);

    const [
        showSensitiveData,
        setShowSensitiveData,
    ] = useState(false);

    const [
        imprint,
        setImprint,
    ] = useState("");

    const accessToken =
        contextAPI.accessToken
        ?? contextAPI.password;

    const currentUserId =
        contextAPI.id;

    const currentUsername =
        contextAPI.username;

    const isEditable =
        Boolean(
            contextAPI.admin,
        );

    const canManageOperatorDocuments =
        Boolean(
            contextAPI.admin
            || contextAPI.operator,
        );

    const showMessageRef =
        useRef(
            appAPI.showMessage,
        );

    const hasLoadedUserRef =
        useRef(false);

    useEffect(() => {
        showMessageRef.current =
            appAPI.showMessage;
    }, [
        appAPI.showMessage,
    ]);

    const closeDialog =
        useCallback(() => {
            setOpenDialog(null);
        }, []);

    const requestReload =
        useCallback(() => {
            setReloadVersion(
                (version) => {
                    return version + 1;
                },
            );
        }, []);

    const runBackendInteraction =
        useCallback((
            request,
            successMessage = null,
        ) => {
            return new Promise(
                (
                    resolve,
                    reject,
                ) => {
                    handleResponse(
                        request,
                        (response) => {
                            if (
                                successMessage
                            ) {
                                showMessageRef
                                    .current(
                                        successMessage,
                                    );
                            }

                            resolve(
                                response,
                            );
                        },
                        (message) => {
                            showMessageRef
                                .current(
                                    message,
                                );

                            reject(
                                new Error(
                                    String(
                                        message,
                                    ),
                                ),
                            );
                        },
                    );
                },
            );
        }, []);

    const validateWithBackend =
        useCallback((
            validator,
            value,
        ) => {
            return new Promise(
                (resolve) => {
                    handleValidationResponse(
                        validator(value),
                        (response) => {
                            resolve(
                                Boolean(
                                    response,
                                ),
                            );
                        },
                        () => {
                            resolve(false);
                        },
                        showMessageRef.current,
                    );
                },
            );
        }, []);

    const updateCurrentContext =
        useCallback(() => {
            if (
                String(
                    currentUserId,
                )
                === String(
                    userId,
                )
            ) {
                contextAPI.update();
            }
        }, [
            contextAPI,
            currentUserId,
            userId,
        ]);

    const saveAndReload =
        useCallback(async (
            action,
            request,
            successMessage,
            options = {},
        ) => {
            setBusyAction(action);

            try {
                await runBackendInteraction(
                    request,
                    successMessage,
                );

                if (
                    options
                        .logoutCurrentUser
                    && String(
                        currentUserId,
                    )
                    === String(
                        userId,
                    )
                ) {
                    contextAPI.logout();

                    return true;
                }

                if (
                    options
                        .updateCurrentUser
                ) {
                    updateCurrentContext();
                }

                requestReload();

                return true;
            } catch {
                return false;
            } finally {
                setBusyAction(null);
            }
        }, [
            contextAPI,
            currentUserId,
            requestReload,
            runBackendInteraction,
            updateCurrentContext,
            userId,
        ]);

    useEffect(() => {
        let requestIsActive =
            true;

        const initialLoad =
            !hasLoadedUserRef
                .current;

        if (!accessToken) {
            setLoading(false);
            setLoadFailed(true);

            showMessageRef.current(
                LABEL.accessTokenMissing,
            );

            return () => {
                requestIsActive =
                    false;
            };
        }

        if (initialLoad) {
            setLoading(true);
            setLoadFailed(false);
        } else {
            setRefreshing(true);
            setRefreshFailed(false);
        }

        handleResponse(
            fetchUserAPI(
                userId,
                accessToken,
            ),
            (loadedUser) => {
                if (!requestIsActive) {
                    return;
                }

                if (
                    !loadedUser
                    || typeof loadedUser
                    !== "object"
                    || Array.isArray(
                        loadedUser,
                    )
                ) {
                    setLoadFailed(true);
                    setLoading(false);
                    setRefreshing(false);

                    return;
                }

                setUser(
                    loadedUser,
                );

                setImprint(
                    formatImprint(
                        loadedUser.imprint,
                    ),
                );

                hasLoadedUserRef
                    .current = true;

                setLoadFailed(false);
                setLoading(false);
                setRefreshing(false);
            },
            (message) => {
                if (!requestIsActive) {
                    return;
                }

                if (initialLoad) {
                    setLoadFailed(true);
                } else {
                    setRefreshFailed(
                        true,
                    );
                }

                setLoading(false);
                setRefreshing(false);

                showMessageRef.current(
                    message,
                );
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        accessToken,
        reloadVersion,
        userId,
    ]);

    useEffect(() => {
        if (!accessToken) {
            return undefined;
        }

        let requestIsActive =
            true;

        handleResponse(
            fetchTagsAPI(
                accessToken,
            ),
            (tags) => {
                if (
                    requestIsActive
                ) {
                    setAvailableTags(
                        Array.isArray(
                            tags,
                        )
                            ? tags
                            : [],
                    );
                }
            },
            () => {
                if (
                    requestIsActive
                ) {
                    setAvailableTags(
                        [],
                    );
                }

                showMessageRef.current(
                    MESSAGE.tagsUnavailable,
                );
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        accessToken,
    ]);

    useEffect(() => {
        if (!accessToken) {
            setRegistrationCourtOptions([]);
            setRegistrationSectionOptions([]);
            setRegistrationOptionsLoading(false);

            return undefined;
        }

        let requestIsActive =
            true;

        let pendingRequests = 2;

        const finishRequest = () => {
            pendingRequests -= 1;

            if (
                requestIsActive
                && pendingRequests === 0
            ) {
                setRegistrationOptionsLoading(
                    false,
                );
            }
        };

        setRegistrationOptionsLoading(true);

        handleResponse(
            fetchRegistrationCourtsAPI(
                accessToken,
            ),
            (courts) => {
                if (requestIsActive) {
                    setRegistrationCourtOptions(
                        mapEnumOptions(
                            courts,
                        ),
                    );
                }

                finishRequest();
            },
            () => {
                if (requestIsActive) {
                    setRegistrationCourtOptions(
                        [],
                    );

                    showMessageRef.current(
                        MESSAGE
                            .registrationCourtsUnavailable,
                    );
                }

                finishRequest();
            },
        );

        handleResponse(
            fetchRegistrationSectionsAPI(
                accessToken,
            ),
            (sections) => {
                if (requestIsActive) {
                    setRegistrationSectionOptions(
                        mapEnumOptions(
                            sections,
                        ),
                    );
                }

                finishRequest();
            },
            () => {
                if (requestIsActive) {
                    setRegistrationSectionOptions(
                        [],
                    );

                    showMessageRef.current(
                        MESSAGE
                            .registrationSectionsUnavailable,
                    );
                }

                finishRequest();
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        accessToken,
    ]);

    useEffect(() => {
        if (
            !accessToken
            || !user
        ) {
            return undefined;
        }

        let requestIsActive =
            true;

        handleResponse(
            fetchUserCanBuyAPI(
                userId,
                currentUsername,
                accessToken,
            ),
            (value) => {
                if (
                    requestIsActive
                ) {
                    setCanBuy(
                        Boolean(
                            value,
                        ),
                    );
                }
            },
            () => {
                if (
                    requestIsActive
                ) {
                    setCanBuy(null);
                }
            },
        );

        handleResponse(
            fetchUserCanSellAPI(
                userId,
                currentUsername,
                accessToken,
            ),
            (value) => {
                if (
                    requestIsActive
                ) {
                    setCanSell(
                        Boolean(
                            value,
                        ),
                    );
                }
            },
            () => {
                if (
                    requestIsActive
                ) {
                    setCanSell(null);
                }
            },
        );

        return () => {
            requestIsActive =
                false;
        };
    }, [
        accessToken,
        currentUsername,
        reloadVersion,
        user,
        userId,
    ]);

    const textDialogConfig =
        useMemo(() => {
            if (!user) {
                return {};
            }

            const backendValidation =
                (validator) => {
                    return async (
                        value,
                    ) => {
                        const valid =
                            await validateWithBackend(
                                validator,
                                value,
                            );

                        return valid
                            ? null
                            : MESSAGE.invalidValue;
                    };
                };

            const percentageValidation =
                async (value) => {
                    const numericValue =
                        Number(value);

                    return (
                        Number.isInteger(
                            numericValue,
                        )
                        && numericValue >= 0
                        && numericValue <= 100
                    )
                        ? null
                        : MESSAGE.invalidPercentage;
                };

            return {
                [DIALOG.businessName]: {
                    title:
                        "Firma ändern",

                    label:
                        LABEL.company,

                    value:
                        user.businessName,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveBusinessNameAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.businessNameSaved,
                },

                [DIALOG.firstName]: {
                    title:
                        "Vorname ändern",

                    label:
                        LABEL.firstName,

                    value:
                        user.firstName,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveFirstNameAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.firstNameSaved,
                },

                [DIALOG.lastName]: {
                    title:
                        "Nachname ändern",

                    label:
                        LABEL.lastName,

                    value:
                        user.lastName,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveLastNameAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.lastNameSaved,
                },

                [DIALOG.additionalDirectors]: {
                    title:
                        "Weitere Geschäftsführer ändern",

                    label:
                        LABEL.additionalDirectors,

                    value:
                        user.additionalDirectors,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveAdditionalDirectorsAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.additionalDirectorsSaved,
                },

                [DIALOG.emailAddress]: {
                    title:
                        "E-Mail-Adresse ändern",

                    label:
                        LABEL.emailAddress,

                    value:
                        user.emailAddress,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyEmailAddress,
                        ),

                    request: (value) => {
                        return saveEmailAddressAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.emailAddressSaved,
                },

                [DIALOG.mobilePhoneNumber]: {
                    title:
                        "Handynummer ändern",

                    label:
                        LABEL.mobilePhoneNumber,

                    value:
                        user.mobilePhoneNumber,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyPhoneNumber,
                        ),

                    request: (value) => {
                        return saveMobilePhoneNumberAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.mobilePhoneNumberSaved,
                },

                [DIALOG.street]: {
                    title:
                        "Straße ändern",

                    label:
                        LABEL.street,

                    value:
                        user.street,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveStreetAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.streetSaved,
                },

                [DIALOG.houseNo]: {
                    title:
                        "Hausnummer ändern",

                    label:
                        LABEL.houseNo,

                    value:
                        user.houseNo,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyHouseNo,
                        ),

                    request: (value) => {
                        return saveHouseNoAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.houseNoSaved,
                },

                [DIALOG.zipCode]: {
                    title:
                        "Postleitzahl ändern",

                    label:
                        LABEL.zipCode,

                    value:
                        user.zipCode,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyZipCode,
                        ),

                    request: (value) => {
                        return saveZipCodeAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.zipCodeSaved,
                },

                [DIALOG.town]: {
                    title:
                        "Ort ändern",

                    label:
                        LABEL.town,

                    value:
                        user.town,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveTownAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.townSaved,
                },

                [DIALOG.country]: {
                    title:
                        "Land ändern",

                    label:
                        LABEL.country,

                    value:
                        user.country,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveCountryAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.countrySaved,
                },

                [DIALOG.bank]: {
                    title:
                        "Bank ändern",

                    label:
                        LABEL.bank,

                    value:
                        user.bank,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveBankAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.bankSaved,
                },

                [DIALOG.bic]: {
                    title:
                        "BIC ändern",

                    label:
                        LABEL.bic,

                    value:
                        user.bic,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveBICAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.bicSaved,
                },

                [DIALOG.iban]: {
                    title:
                        "IBAN ändern",

                    label:
                        LABEL.iban,

                    value:
                        user.iban,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveIBANAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.ibanSaved,
                },

                [DIALOG.vatId]: {
                    title:
                        "USt-ID ändern",

                    label:
                        LABEL.vatId,

                    value:
                        user.vatId
                        ?? user.vatID,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyUstID,
                        ),

                    request: (value) => {
                        return saveVatIdAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.vatIdSaved,
                },

                [DIALOG.taxId]: {
                    title:
                        "Steuernummer ändern",

                    label:
                        LABEL.taxId,

                    value:
                        user.taxId
                        ?? user.taxID,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveTaxIdAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.taxIdSaved,
                },

                [DIALOG.vatRate]: {
                    title:
                        "Umsatzsteuersatz ändern",

                    label:
                        LABEL.vatRate,

                    value:
                        user.vatRate,

                    inputMode:
                        "numeric",

                    validate:
                        percentageValidation,

                    request: (value) => {
                        return saveVatRateAPI(
                            userId,
                            accessToken,
                            Number(value),
                        );
                    },

                    message:
                        MESSAGE.vatRateSaved,
                },

                [DIALOG.invoicePrefix]: {
                    title:
                        "Rechnungspräfix ändern",

                    label:
                        LABEL.invoicePrefix,

                    value:
                        user.invoicePrefix,

                    validate:
                        backendValidation(
                            validateNonEmptyAlphanumericString,
                        ),

                    request: (value) => {
                        return saveInvoicePrefixAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.invoicePrefixSaved,
                },

                [DIALOG.stripeId]: {
                    title:
                        "Stripe-Konto-ID ändern",

                    label:
                        LABEL.stripeId,

                    value:
                        user.stripeId,

                    validate:
                        backendValidation(
                            validatePossiblyEmptyStripeAccountId,
                        ),

                    request: (value) => {
                        return saveStripeIdAPI(
                            userId,
                            accessToken,
                            value,
                        );
                    },

                    message:
                        MESSAGE.stripeIdSaved,
                },

                [DIALOG.transactionFee]: {
                    title:
                        "Transaktionsgebühr ändern",

                    label:
                        LABEL.transactionFee,

                    value:
                        user.transactionFees,

                    inputMode:
                        "numeric",

                    validate:
                        percentageValidation,

                    request: (value) => {
                        return saveTransactionFeeAPI(
                            userId,
                            accessToken,
                            Number(value),
                        );
                    },

                    message:
                        MESSAGE.transactionFeeSaved,
                },
            };
        }, [
            accessToken,
            user,
            userId,
            validateWithBackend,
        ]);

    const activeTextDialog =
        textDialogConfig[
            openDialog
        ] ?? null;

    const saveTextValue =
        useCallback((value) => {
            if (!activeTextDialog) {
                return Promise
                    .resolve(false);
            }

            return saveAndReload(
                openDialog,
                activeTextDialog
                    .request(value),
                activeTextDialog
                    .message,
                {
                    updateCurrentUser:
                        true,
                },
            );
        }, [
            activeTextDialog,
            openDialog,
            saveAndReload,
        ]);

    const validateRegistrationNumber =
        useCallback(async (
            value,
        ) => {
            const valid =
                await validateWithBackend(
                    validateNonEmptyAlphanumericString,
                    value,
                );

            return valid
                ? null
                : MESSAGE.invalidValue;
        }, [
            validateWithBackend,
        ]);

    const saveRegistrationData =
        useCallback(async (
            section,
            number,
        ) => {
            setBusyAction(
                ACTION.registrationData,
            );

            try {
                await runBackendInteraction(
                    saveRegistrationSectionAPI(
                        userId,
                        accessToken,
                        section,
                    ),
                );

                await runBackendInteraction(
                    saveRegistrationNoAPI(
                        userId,
                        accessToken,
                        number,
                    ),
                    MESSAGE.registrationDataSaved,
                );

                updateCurrentContext();
                requestReload();

                return true;
            } catch {
                return false;
            } finally {
                setBusyAction(null);
            }
        }, [
            accessToken,
            requestReload,
            runBackendInteraction,
            updateCurrentContext,
            userId,
        ]);

    const uploadPdf =
        useCallback((
            action,
            file,
            request,
            successMessage,
        ) => {
            if (!isPdfFile(file)) {
                showMessageRef.current(
                    MESSAGE.invalidPdf,
                );

                return;
            }

            void saveAndReload(
                action,
                request(file),
                successMessage,
            );
        }, [
            saveAndReload,
        ]);

    if (
        loading
        && !user
    ) {
        return (
            <UserEditorSkeleton />
        );
    }

    if (
        loadFailed
        || !user
    ) {
        return (
            <Alert severity="error">
                {LABEL.loadingError}
            </Alert>
        );
    }

    const fullName = [
        user.firstName,
        user.lastName,
    ]
        .filter(Boolean)
        .join(" ");

    const avatarText = [
        user.firstName,
        user.lastName,
    ]
        .filter(Boolean)
        .map((part) => {
            return part.charAt(0);
        })
        .join("")
        || user.username
            ?.charAt(0)
        || "?";

    const isBusinessUser =
        Boolean(
            user.operator
            || user.seller,
        );

    const hasTags =
        Boolean(
            user.buyer
            || user.seller,
        );

    const isRegistered =
        Boolean(
            user.registered,
        );

    const userTags =
        Array.isArray(
            user.tags,
        )
            ? user.tags
            : [];

    const headerBackground =
        user.disabled
            ? "#5f6368"
            : PRIMARY_COLOR;

    const headerPaperStyle = {
        ...STYLE.headerPaper,
        boxShadow:
            `0 16px 42px ${alpha(
                headerBackground,
                0.20,
            )}`,
    };

    return (
        <>
            <style>
                {`
                    .MuiModal-root {
                        z-index: ${EDITOR_DIALOG_Z_INDEX} !important;
                    }
                `}
            </style>

            <Stack
                spacing={4}
                sx={STYLE.page}
            >
                <Paper sx={headerPaperStyle}>
                    <Box
                        sx={{
                            ...STYLE.headerBar,
                            backgroundColor:
                                headerBackground,
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                md: "row",
                            }}
                            alignItems={{
                                xs: "flex-start",
                                md: "center",
                            }}
                            spacing={2.5}
                            sx={STYLE.headerContent}
                        >
                            <Avatar
                                src={
                                    user.portraitUrl
                                    || undefined
                                }
                                alt={
                                    fullName
                                    || user.username
                                }
                                sx={STYLE.headerAvatar}
                            >
                                {
                                    avatarText
                                        .toUpperCase()
                                }
                            </Avatar>

                            <Box
                                sx={{
                                    flexGrow: 1,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    component="h1"
                                    sx={STYLE.headerTitle}
                                >
                                    {
                                        fullName
                                        || user.username
                                    }
                                </Typography>

                                <Typography
                                    sx={STYLE.headerSubtitle}
                                >
                                    @
                                    {
                                        displayValue(
                                            user.username,
                                        )
                                    }

                                    {
                                        user.businessName
                                            ? ` · ${user.businessName}`
                                            : ""
                                    }
                                </Typography>

                                <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{
                                        marginTop:
                                            "16px",
                                    }}
                                >
                                    {user.admin && (
                                        <Chip
                                            size="small"
                                            label={
                                                LABEL.administrator
                                            }
                                            sx={STYLE.headerChip}
                                        />
                                    )}

                                    {user.buyer && (
                                        <Chip
                                            size="small"
                                            label={LABEL.buyer}
                                            sx={STYLE.headerChip}
                                        />
                                    )}

                                    {user.seller && (
                                        <Chip
                                            size="small"
                                            label={LABEL.seller}
                                            sx={STYLE.headerChip}
                                        />
                                    )}

                                    {user.operator && (
                                        <Chip
                                            size="small"
                                            label={LABEL.operator}
                                            sx={STYLE.headerChip}
                                        />
                                    )}
                                </Stack>
                            </Box>

                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                <Chip
                                    size="small"
                                    icon={
                                        user.disabled
                                            ? (
                                                <BlockOutlinedIcon />
                                            )
                                            : undefined
                                    }
                                    label={
                                        user.disabled
                                            ? LABEL.disabled
                                            : LABEL.active
                                    }
                                    sx={STYLE.headerChip}
                                />

                                {refreshing && (
                                    <Chip
                                        size="small"
                                        icon={
                                            <CircularProgress
                                                size={14}
                                                color="inherit"
                                            />
                                        }
                                        label={LABEL.refreshing}
                                        sx={STYLE.headerChip}
                                    />
                                )}
                            </Stack>
                        </Stack>
                    </Box>
                </Paper>

                {refreshFailed && (
                    <Alert severity="warning">
                        {LABEL.refreshError}
                    </Alert>
                )}

                <EditorSection
                    title={
                        LABEL.profile
                    }
                >
                    <Grid
                        container
                        spacing={3}
                        alignItems="stretch"
                    >
                        <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                        >
                            <OverviewCard
                                title={
                                    LABEL.person
                                }
                                icon={
                                    <PersonOutlineIcon
                                        fontSize="small"
                                    />
                                }
                            >
                                <Stack spacing={2.25}>
                                    <InfoRow
                                        label={
                                            LABEL.company
                                        }
                                        value={
                                            user.businessName
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.businessName,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.firstName
                                        }
                                        value={
                                            user.firstName
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.firstName,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.lastName
                                        }
                                        value={
                                            user.lastName
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.lastName,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.additionalDirectors
                                        }
                                        value={
                                            user.additionalDirectors
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.additionalDirectors,
                                            );
                                        }}
                                    />
                                </Stack>
                            </OverviewCard>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                        >
                            <OverviewCard
                                title={
                                    LABEL.contact
                                }
                                icon={
                                    <ContactMailOutlinedIcon
                                        fontSize="small"
                                    />
                                }
                            >
                                <Stack spacing={2.25}>
                                    <InfoRow
                                        label={
                                            LABEL.emailAddress
                                        }
                                        value={
                                            user.emailAddress
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.emailAddress,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.mobilePhoneNumber
                                        }
                                        value={
                                            user.mobilePhoneNumber
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.mobilePhoneNumber,
                                            );
                                        }}
                                    />
                                </Stack>
                            </OverviewCard>
                        </Grid>

                        <Grid
                            item
                            xs={12}
                            md={6}
                            lg={4}
                        >
                            <OverviewCard
                                title={
                                    LABEL.address
                                }
                                icon={
                                    <HomeOutlinedIcon
                                        fontSize="small"
                                    />
                                }
                            >
                                <Stack spacing={2.25}>
                                    <InfoRow
                                        label={
                                            LABEL.street
                                        }
                                        value={
                                            user.street
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.street,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.houseNo
                                        }
                                        value={
                                            user.houseNo
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.houseNo,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.zipCode
                                        }
                                        value={
                                            user.zipCode
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.zipCode,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.town
                                        }
                                        value={
                                            user.town
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.town,
                                            );
                                        }}
                                    />

                                    <InfoRow
                                        label={
                                            LABEL.country
                                        }
                                        value={
                                            user.country
                                        }
                                        editable={
                                            isEditable
                                        }
                                        onEdit={() => {
                                            setOpenDialog(
                                                DIALOG.country,
                                            );
                                        }}
                                    />
                                </Stack>
                            </OverviewCard>
                        </Grid>

                        {hasTags && (
                            <Grid
                                item
                                xs={12}
                            >
                                <OverviewCard
                                    title={
                                        LABEL.tags
                                    }
                                    icon={
                                        <LabelOutlinedIcon
                                            fontSize="small"
                                        />
                                    }
                                    action={
                                        isEditable
                                            ? (
                                                <Button
                                                    size="small"
                                                    startIcon={
                                                        <EditOutlinedIcon />
                                                    }
                                                    onClick={() => {
                                                        setOpenDialog(
                                                            DIALOG.tags,
                                                        );
                                                    }}
                                                >
                                                    {LABEL.edit}
                                                </Button>
                                            )
                                            : null
                                    }
                                >
                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        useFlexGap
                                        flexWrap="wrap"
                                    >
                                        {
                                            userTags.length > 0
                                                ? userTags.map(
                                                    (tag) => (
                                                        <Chip
                                                            key={
                                                                getTagId(
                                                                    tag,
                                                                )
                                                                ?? getTagLabel(
                                                                    tag,
                                                                )
                                                            }
                                                            label={
                                                                getTagLabel(
                                                                    tag,
                                                                )
                                                            }
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{
                                                                borderRadius: 1.5,
                                                            }}
                                                        />
                                                    ),
                                                )
                                                : (
                                                    <Typography
                                                        color="text.secondary"
                                                    >
                                                        {EMPTY_VALUE}
                                                    </Typography>
                                                )
                                        }
                                    </Stack>
                                </OverviewCard>
                            </Grid>
                        )}
                    </Grid>
                </EditorSection>

                {isBusinessUser && (
                    <EditorSection
                        title={
                            LABEL.businessDetails
                        }
                    >
                        <Grid
                            container
                            spacing={3}
                            alignItems="stretch"
                        >
                            <Grid
                                item
                                xs={12}
                                lg={6}
                                xl={6}
                            >
                                <OverviewCard
                                    title={
                                        LABEL.bankAndTax
                                    }
                                    icon={
                                        <AccountBalanceOutlinedIcon
                                            fontSize="small"
                                        />
                                    }
                                    action={
                                        <Tooltip
                                            title={
                                                showSensitiveData
                                                    ? LABEL.hideSensitive
                                                    : LABEL.showSensitive
                                            }
                                        >
                                            <IconButton
                                                size="small"
                                                onClick={() => {
                                                    setShowSensitiveData(
                                                        (
                                                            visible,
                                                        ) => {
                                                            return !visible;
                                                        },
                                                    );
                                                }}
                                            >
                                                {
                                                    showSensitiveData
                                                        ? (
                                                            <VisibilityOffOutlinedIcon />
                                                        )
                                                        : (
                                                            <VisibilityOutlinedIcon />
                                                        )
                                                }
                                            </IconButton>
                                        </Tooltip>
                                    }
                                >
                                    <Grid
                                        container
                                        spacing={2.5}
                                    >
                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.bank
                                                }
                                                value={
                                                    user.bank
                                                }
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.bank,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.bic
                                                }
                                                value={
                                                    maskValue(
                                                        user.bic,
                                                        showSensitiveData,
                                                        3,
                                                    )
                                                }
                                                monospace
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.bic,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.iban
                                                }
                                                value={
                                                    maskIban(
                                                        user.iban,
                                                        showSensitiveData,
                                                    )
                                                }
                                                monospace
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.iban,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.taxId
                                                }
                                                value={
                                                    maskValue(
                                                        user.taxId
                                                        ?? user.taxID,
                                                        showSensitiveData,
                                                    )
                                                }
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.taxId,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                            sm={6}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.vatId
                                                }
                                                value={
                                                    maskValue(
                                                        user.vatId
                                                        ?? user.vatID,
                                                        showSensitiveData,
                                                    )
                                                }
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.vatId,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                        >
                                            <InfoRow
                                                label={
                                                    LABEL.vat
                                                }
                                                value={
                                                    user.taxExemption
                                                        ? "Befreit nach § 19 UStG"
                                                        : formatPercentage(
                                                            user.vatRate,
                                                        )
                                                }
                                                editable={
                                                    isEditable
                                                    && !user.taxExemption
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.vatRate,
                                                    );
                                                }}
                                            />
                                        </Grid>

                                        <Grid
                                            item
                                            xs={12}
                                        >
                                            <FormControlLabel
                                                control={
                                                    <Checkbox
                                                        checked={
                                                            Boolean(
                                                                user.taxExemption,
                                                            )
                                                        }
                                                        disabled={
                                                            !isEditable
                                                            || busyAction
                                                            !== null
                                                        }
                                                        onChange={(
                                                            event,
                                                        ) => {
                                                            void saveAndReload(
                                                                ACTION
                                                                    .taxExemption,
                                                                saveTaxExemptionAPI(
                                                                    userId,
                                                                    accessToken,
                                                                    event.target
                                                                        .checked,
                                                                ),
                                                                MESSAGE
                                                                    .taxExemptionSaved,
                                                                {
                                                                    updateCurrentUser:
                                                                        true,
                                                                },
                                                            );
                                                        }}
                                                    />
                                                }
                                                label={
                                                    LABEL.taxExemption
                                                }
                                            />
                                        </Grid>
                                    </Grid>
                                </OverviewCard>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                                xl={3}
                            >
                                <OverviewCard
                                    title={
                                        LABEL.invoiceDetails
                                    }
                                    icon={
                                        <ReceiptLongOutlinedIcon
                                            fontSize="small"
                                        />
                                    }
                                >
                                    <Stack spacing={2.5}>
                                        <InfoRow
                                            label={
                                                LABEL.invoicePrefix
                                            }
                                            value={
                                                user.invoicePrefix
                                            }
                                            editable={
                                                isEditable
                                            }
                                            onEdit={() => {
                                                setOpenDialog(
                                                    DIALOG.invoicePrefix,
                                                );
                                            }}
                                        />

                                        {user.seller && (
                                            <InfoRow
                                                label={
                                                    LABEL.stripeId
                                                }
                                                value={
                                                    user.stripeId
                                                }
                                                monospace
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.stripeId,
                                                    );
                                                }}
                                            />
                                        )}

                                        {user.seller && (
                                            <TransactionFeeKpi
                                                value={
                                                    user.transactionFees
                                                }
                                                editable={
                                                    isEditable
                                                }
                                                onEdit={() => {
                                                    setOpenDialog(
                                                        DIALOG.transactionFee,
                                                    );
                                                }}
                                            />
                                        )}
                                    </Stack>
                                </OverviewCard>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                                md={6}
                                lg={3}
                                xl={3}
                            >
                                <OverviewCard
                                    title={
                                        LABEL.registration
                                    }
                                    icon={
                                        <GavelOutlinedIcon
                                            fontSize="small"
                                        />
                                    }
                                >
                                    <Stack spacing={2.5}>
                                        <InfoRow
                                            label={
                                                LABEL.registrationCourt
                                            }
                                            value={
                                                formatRegistrationCourt(
                                                    user.registrationCourt,
                                                    registrationCourtOptions,
                                                )
                                            }
                                            editable={
                                                isEditable
                                            }
                                            onEdit={() => {
                                                setOpenDialog(
                                                    DIALOG.registrationCourt,
                                                );
                                            }}
                                        />

                                        <InfoRow
                                            label={
                                                LABEL.registrationNo
                                            }
                                            value={
                                                formatRegistrationNumber(
                                                    user.registrationSection,
                                                    user.registrationNo,
                                                )
                                            }
                                            editable={
                                                isEditable
                                            }
                                            onEdit={() => {
                                                setOpenDialog(
                                                    DIALOG.registrationData,
                                                );
                                            }}
                                        />

                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    checked={
                                                        isRegistered
                                                    }
                                                    disabled={
                                                        !isEditable
                                                        || busyAction
                                                        !== null
                                                    }
                                                    onChange={(
                                                        event,
                                                    ) => {
                                                        void saveAndReload(
                                                            ACTION.registered,
                                                            saveRegisteredAPI(
                                                                userId,
                                                                accessToken,
                                                                event.target
                                                                    .checked,
                                                            ),
                                                            MESSAGE
                                                                .registeredSaved,
                                                            {
                                                                updateCurrentUser:
                                                                    true,
                                                            },
                                                        );
                                                    }}
                                                />
                                            }
                                            label={
                                                LABEL.registered
                                            }
                                        />
                                    </Stack>
                                </OverviewCard>
                            </Grid>

                            <Grid
                                item
                                xs={12}
                            >
                                <OverviewCard
                                    title={
                                        LABEL.imprint
                                    }
                                    icon={
                                        <DescriptionOutlinedIcon
                                            fontSize="small"
                                        />
                                    }
                                    action={
                                        isEditable
                                            ? (
                                                <Button
                                                    size="small"
                                                    startIcon={
                                                        <EditOutlinedIcon />
                                                    }
                                                    onClick={() => {
                                                        setOpenDialog(
                                                            DIALOG.imprint,
                                                        );
                                                    }}
                                                >
                                                    {LABEL.edit}
                                                </Button>
                                            )
                                            : null
                                    }
                                >
                                    <Typography
                                        variant="body2"
                                        component="pre"
                                        sx={STYLE.imprint}
                                    >
                                        {
                                            displayValue(
                                                formatImprint(
                                                    user.imprint,
                                                ),
                                            )
                                        }
                                    </Typography>
                                </OverviewCard>
                            </Grid>
                        </Grid>
                    </EditorSection>
                )}

                {user.seller && (
                    <EditorSection
                        title={
                            LABEL.sellerDocuments
                        }
                    >
                        <OverviewCard
                            title={
                                LABEL.documents
                            }
                            icon={
                                <DescriptionOutlinedIcon
                                    fontSize="small"
                                />
                            }
                        >
                            <Stack spacing={0}>
                                <DocumentRow
                                    title={
                                        LABEL.sellerTerms
                                    }
                                    data={
                                        user.sellerTerms
                                    }
                                    fileName="Verkaeufer-AGB.pdf"
                                    canUpload={
                                        isEditable
                                    }
                                    busy={
                                        busyAction
                                        === ACTION.sellerTerms
                                    }
                                    onUpload={(
                                        file,
                                    ) => {
                                        uploadPdf(
                                            ACTION.sellerTerms,
                                            file,
                                            (pdf) => {
                                                return saveSellerTermsAPI(
                                                    userId,
                                                    accessToken,
                                                    pdf,
                                                );
                                            },
                                            MESSAGE.sellerTermsSaved,
                                        );
                                    }}
                                />

                                <Divider />

                                <DocumentRow
                                    title={
                                        LABEL.sellerPrivacyPolicy
                                    }
                                    data={
                                        user.sellerPrivacyPolicy
                                    }
                                    fileName="Verkaeufer-Datenschutzerklaerung.pdf"
                                    canUpload={
                                        isEditable
                                    }
                                    busy={
                                        busyAction
                                        === ACTION.sellerPrivacyPolicy
                                    }
                                    onUpload={(
                                        file,
                                    ) => {
                                        uploadPdf(
                                            ACTION.sellerPrivacyPolicy,
                                            file,
                                            (pdf) => {
                                                return saveSellerPrivacyPolicyAPI(
                                                    userId,
                                                    accessToken,
                                                    pdf,
                                                );
                                            },
                                            MESSAGE
                                                .sellerPrivacyPolicySaved,
                                        );
                                    }}
                                />
                            </Stack>
                        </OverviewCard>
                    </EditorSection>
                )}

                {user.operator && (
                    <EditorSection
                        title={
                            LABEL.operatorDocuments
                        }
                    >
                        <OverviewCard
                            title={
                                LABEL.documents
                            }
                            icon={
                                <DescriptionOutlinedIcon
                                    fontSize="small"
                                />
                            }
                        >
                            <Stack spacing={0}>
                                <DocumentRow
                                    title={
                                        LABEL.operatorTerms
                                    }
                                    data={
                                        user.operatorTerms
                                    }
                                    fileName="Operator-AGB.pdf"
                                    canUpload={
                                        canManageOperatorDocuments
                                    }
                                    busy={
                                        busyAction
                                        === ACTION.operatorTerms
                                    }
                                    onUpload={(
                                        file,
                                    ) => {
                                        uploadPdf(
                                            ACTION.operatorTerms,
                                            file,
                                            (pdf) => {
                                                return saveOperatorTermsAPI(
                                                    accessToken,
                                                    pdf,
                                                );
                                            },
                                            MESSAGE.operatorTermsSaved,
                                        );
                                    }}
                                />

                                <Divider />

                                <DocumentRow
                                    title={
                                        LABEL.operatorPrivacyPolicy
                                    }
                                    data={
                                        user.operatorPrivacyPolicy
                                    }
                                    fileName="Operator-Datenschutzerklaerung.pdf"
                                    canUpload={
                                        canManageOperatorDocuments
                                    }
                                    busy={
                                        busyAction
                                        === ACTION.operatorPrivacyPolicy
                                    }
                                    onUpload={(
                                        file,
                                    ) => {
                                        uploadPdf(
                                            ACTION.operatorPrivacyPolicy,
                                            file,
                                            (pdf) => {
                                                return saveOperatorPrivacyPolicyAPI(
                                                    accessToken,
                                                    pdf,
                                                );
                                            },
                                            MESSAGE
                                                .operatorPrivacyPolicySaved,
                                        );
                                    }}
                                />
                            </Stack>
                        </OverviewCard>
                    </EditorSection>
                )}

                <EditorSection
                    title={
                        LABEL.rolesAndStatus
                    }
                >
                    <OverviewCard
                        title={
                            LABEL.permissions
                        }
                        icon={
                            <BadgeOutlinedIcon
                                fontSize="small"
                            />
                        }
                    >
                        <Stack spacing={2.5}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                Boolean(
                                                    user.admin,
                                                )
                                            }
                                            disabled={
                                                !isEditable
                                                || busyAction
                                                !== null
                                            }
                                            onChange={(
                                                event,
                                            ) => {
                                                void saveAndReload(
                                                    ACTION.adminRole,
                                                    saveIsAdminAPI(
                                                        userId,
                                                        accessToken,
                                                        event.target
                                                            .checked,
                                                    ),
                                                    MESSAGE.adminRoleSaved,
                                                    {
                                                        updateCurrentUser:
                                                            true,
                                                    },
                                                );
                                            }}
                                        />
                                    }
                                    label={
                                        LABEL.administrator
                                    }
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                Boolean(
                                                    user.buyer,
                                                )
                                            }
                                            disabled={
                                                !isEditable
                                                || busyAction
                                                !== null
                                            }
                                            onChange={(
                                                event,
                                            ) => {
                                                void saveAndReload(
                                                    ACTION.buyerRole,
                                                    saveIsBuyerAPI(
                                                        userId,
                                                        accessToken,
                                                        event.target
                                                            .checked,
                                                    ),
                                                    MESSAGE.buyerRoleSaved,
                                                    {
                                                        updateCurrentUser:
                                                            true,
                                                    },
                                                );
                                            }}
                                        />
                                    }
                                    label={
                                        LABEL.buyer
                                    }
                                />

                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={
                                                Boolean(
                                                    user.seller,
                                                )
                                            }
                                            disabled={
                                                !isEditable
                                                || busyAction
                                                !== null
                                            }
                                            onChange={(
                                                event,
                                            ) => {
                                                void saveAndReload(
                                                    ACTION.sellerRole,
                                                    saveIsSellerAPI(
                                                        userId,
                                                        accessToken,
                                                        event.target
                                                            .checked,
                                                    ),
                                                    MESSAGE.sellerRoleSaved,
                                                    {
                                                        updateCurrentUser:
                                                            true,
                                                    },
                                                );
                                            }}
                                        />
                                    }
                                    label={
                                        LABEL.seller
                                    }
                                />

                                {user.operator && (
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked
                                                disabled
                                            />
                                        }
                                        label={
                                            `${LABEL.operator} (intern verwaltet)`
                                        }
                                    />
                                )}
                            </FormGroup>

                            <Divider />

                            <Stack
                                direction="row"
                                spacing={1}
                                useFlexGap
                                flexWrap="wrap"
                            >
                                <StatusChip
                                    value={
                                        canBuy
                                    }
                                    positiveLabel={
                                        LABEL.canBuy
                                    }
                                    negativeLabel={
                                        LABEL.cannotBuy
                                    }
                                />

                                <StatusChip
                                    value={
                                        canSell
                                    }
                                    positiveLabel={
                                        LABEL.canSell
                                    }
                                    negativeLabel={
                                        LABEL.cannotSell
                                    }
                                />

                                <Chip
                                    size="small"
                                    label={
                                        user.disabled
                                            ? LABEL.disabled
                                            : LABEL.active
                                    }
                                    color={
                                        user.disabled
                                            ? "default"
                                            : "success"
                                    }
                                    variant={
                                        user.disabled
                                            ? "outlined"
                                            : "filled"
                                    }
                                />
                            </Stack>
                        </Stack>
                    </OverviewCard>
                </EditorSection>

                <EditorSection
                    title={
                        LABEL.login
                    }
                >
                    <OverviewCard
                        title={
                            LABEL.loginData
                        }
                        icon={
                            <LoginOutlinedIcon
                                fontSize="small"
                            />
                        }
                    >
                        <Stack spacing={2.25}>
                            <InfoRow
                                label={
                                    LABEL.username
                                }
                                value={
                                    user.username
                                }
                                editable={
                                    isEditable
                                }
                                onEdit={() => {
                                    setOpenDialog(
                                        DIALOG.username,
                                    );
                                }}
                            />

                            <InfoRow
                                label={
                                    LABEL.password
                                }
                                value={
                                    PASSWORD_MASK
                                }
                                editable={
                                    isEditable
                                }
                                onEdit={() => {
                                    setOpenDialog(
                                        DIALOG.password,
                                    );
                                }}
                            />

                            {user.seller && (
                                <Box>
                                    <Button
                                        variant="outlined"
                                        onClick={() => {
                                            setOpenDialog(
                                                DIALOG.partnerManagement,
                                            );
                                        }}
                                    >
                                        {
                                            LABEL.partnerManagement
                                        }
                                    </Button>
                                </Box>
                            )}
                        </Stack>
                    </OverviewCard>
                </EditorSection>

                <EditorSection
                    title={
                        LABEL.dangerZone
                    }
                >
                    <Paper
                        variant="outlined"
                        sx={{
                            ...STYLE.card,

                            height: "auto",

                            borderColor:
                                "error.light",

                            padding: {
                                xs: "18px",
                                sm: "24px",
                            },
                        }}
                    >
                        <Stack
                            direction={{
                                xs: "column",
                                sm: "row",
                            }}
                            spacing={2}
                            alignItems={{
                                xs:
                                    "flex-start",

                                sm:
                                    "center",
                            }}
                            justifyContent="space-between"
                        >
                            <Typography
                                variant="body2"
                                color="text.secondary"
                            >
                                {
                                    LABEL.disableDescription
                                }
                            </Typography>

                            <Button
                                color="error"
                                variant="outlined"
                                disabled={
                                    !isEditable
                                    || !user.canBeDisabled
                                    || user.disabled
                                    || busyAction
                                    !== null
                                }
                                startIcon={
                                    busyAction
                                    === ACTION.disable
                                        ? (
                                            <CircularProgress
                                                size={18}
                                                color="inherit"
                                            />
                                        )
                                        : (
                                            <BlockOutlinedIcon />
                                        )
                                }
                                onClick={() => {
                                    void saveAndReload(
                                        ACTION.disable,
                                        disableAPI(
                                            userId,
                                            accessToken,
                                        ),
                                        MESSAGE.disabled,
                                        {
                                            logoutCurrentUser:
                                                true,
                                        },
                                    );
                                }}
                            >
                                {LABEL.disable}
                            </Button>
                        </Stack>
                    </Paper>
                </EditorSection>
            </Stack>

            <EditValueDialog
                open={
                    Boolean(
                        activeTextDialog,
                    )
                }
                title={
                    activeTextDialog
                        ?.title
                    ?? LABEL.edit
                }
                label={
                    activeTextDialog
                        ?.label
                    ?? LABEL.edit
                }
                initialValue={
                    activeTextDialog
                        ?.value
                    ?? ""
                }
                inputMode={
                    activeTextDialog
                        ?.inputMode
                }
                validate={
                    activeTextDialog
                        ?.validate
                }
                onClose={
                    closeDialog
                }
                onSave={
                    saveTextValue
                }
            />

            <SelectValueDialog
                open={
                    openDialog
                    === DIALOG.registrationCourt
                }
                title="Registergericht ändern"
                label={
                    LABEL.registrationCourt
                }
                initialValue={
                    normalizeRegistrationCourt(
                        user.registrationCourt,
                    )
                }
                options={
                    registrationCourtOptions
                }
                loading={
                    registrationOptionsLoading
                }
                emptyMessage={
                    "Keine Registergerichte verfügbar."
                }
                onClose={
                    closeDialog
                }
                onSave={(value) => {
                    const normalizedValue =
                        normalizeRegistrationCourt(
                            value,
                        );

                    return saveAndReload(
                        DIALOG.registrationCourt,
                        saveRegistrationCourtAPI(
                            userId,
                            accessToken,
                            normalizedValue,
                        ),
                        MESSAGE.registrationCourtSaved,
                        {
                            updateCurrentUser:
                                true,
                        },
                    );
                }}
            />

            <RegistrationDataDialog
                open={
                    openDialog
                    === DIALOG.registrationData
                }
                initialSection={
                    user.registrationSection
                }
                initialNumber={
                    user.registrationNo
                }
                sections={
                    registrationSectionOptions
                }
                sectionsLoading={
                    registrationOptionsLoading
                }
                validateNumber={
                    validateRegistrationNumber
                }
                onClose={
                    closeDialog
                }
                onSave={
                    saveRegistrationData
                }
            />

            <TagsDialog
                open={
                    openDialog
                    === DIALOG.tags
                }
                tags={
                    availableTags
                }
                selectedTags={
                    userTags
                }
                onClose={
                    closeDialog
                }
                onSave={(tagIds) => {
                    return saveAndReload(
                        ACTION.tags,
                        saveTagsAPI(
                            userId,
                            accessToken,
                            tagIds,
                        ),
                        MESSAGE.tagsSaved,
                        {
                            updateCurrentUser:
                                true,
                        },
                    );
                }}
            />

            <EditUsernameDialog
                appAPI={appAPI}
                contextAPI={
                    contextAPI
                }
                currentUsername={
                    user.username
                }
                dialogTitle="Benutzername ändern"
                open={
                    openDialog
                    === DIALOG.username
                }
                onClose={
                    closeDialog
                }
                onSave={async (
                    value,
                ) => {
                    const saved =
                        await saveAndReload(
                            DIALOG.username,
                            saveUsernameAPI(
                                userId,
                                accessToken,
                                value,
                            ),
                            MESSAGE.usernameSaved,
                            {
                                logoutCurrentUser:
                                    true,
                            },
                        );

                    if (saved) {
                        closeDialog();
                    }

                    return saved;
                }}
            />

            <EditPasswordDialog
                appAPI={appAPI}
                dialogTitle="Passwort ändern"
                open={
                    openDialog
                    === DIALOG.password
                }
                onClose={
                    closeDialog
                }
                onSave={async (
                    value,
                ) => {
                    const saved =
                        await saveAndReload(
                            DIALOG.password,
                            savePasswordAPI(
                                userId,
                                accessToken,
                                value,
                            ),
                            MESSAGE.passwordSaved,
                            {
                                logoutCurrentUser:
                                    true,
                            },
                        );

                    if (saved) {
                        closeDialog();
                    }

                    return saved;
                }}
            />

            <FeatureDialog
                open={
                    openDialog
                    === DIALOG.imprint
                }
                title={
                    LABEL.imprint
                }
                onClose={
                    closeDialog
                }
            >
                <Stack spacing={2}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={12}
                        value={imprint}
                        label={
                            LABEL.imprint
                        }
                        disabled={
                            !isEditable
                        }
                        onChange={(
                            event,
                        ) => {
                            setImprint(
                                event.target
                                    .value,
                            );
                        }}
                    />

                    {isEditable && (
                        <Box>
                            <Button
                                variant="contained"
                                disabled={
                                    busyAction !== null
                                }
                                startIcon={
                                    busyAction
                                    === ACTION.imprint
                                        ? (
                                            <CircularProgress
                                                size={18}
                                                color="inherit"
                                            />
                                        )
                                        : undefined
                                }
                                onClick={async () => {
                                    const valid =
                                        await validateWithBackend(
                                            validateAcceptAny,
                                            imprint,
                                        );

                                    if (!valid) {
                                        showMessageRef
                                            .current(
                                                MESSAGE.invalidValue,
                                            );

                                        return;
                                    }

                                    const saved =
                                        await saveAndReload(
                                            ACTION.imprint,
                                            saveImprintAPI(
                                                userId,
                                                accessToken,
                                                preprocessImprint(
                                                    imprint,
                                                ),
                                            ),
                                            MESSAGE.imprintSaved,
                                            {
                                                updateCurrentUser:
                                                    true,
                                            },
                                        );

                                    if (saved) {
                                        closeDialog();
                                    }
                                }}
                            >
                                {LABEL.save}
                            </Button>
                        </Box>
                    )}
                </Stack>
            </FeatureDialog>

            <FeatureDialog
                open={
                    openDialog
                    === DIALOG.partnerManagement
                }
                title={
                    LABEL.partnerManagement
                }
                onClose={
                    closeDialog
                }
                maxWidth="xl"
            >
                <PartnerManagement
                    appAPI={appAPI}
                    contextAPI={
                        contextAPI
                    }
                    userId={userId}
                />
            </FeatureDialog>
        </>
    );
}
